'use client';

import { useEffect, useState } from 'react';
import { sessionClient } from 'services/api/client';
import type { SessionInfosResponse, UserDataResponse } from 'services/types';
import { isUserAuthenticated, logger, parseJwtToken, withTimeout } from 'utils';
import { SESSION_JWT_STATUS, SESSION_REQUEST_TIMEOUT_MS } from '../useSessionJwt/constants';
import { useSessionJwt } from '../useSessionJwt/useSessionJwt';
import { CLIENT_SESSION_STATUS } from './constants';
import { readAttributionParams } from './readAttributionParams';
import type { ClientSessionStatus } from './types';

export type ClientSessionState = {
  status: ClientSessionStatus;
  isAuthenticated: boolean;
  uuid: string | null;
  sessionInfos: SessionInfosResponse | null;
  userData: UserDataResponse | null;
};

const INITIAL_STATE: ClientSessionState = {
  status: CLIENT_SESSION_STATUS.LOADING,
  isAuthenticated: false,
  uuid: null,
  sessionInfos: null,
  userData: null,
};

type LoadOptions = {
  /** setSessionInfos (p_ref / voucher) runs once per page; a reload after login only reads the session infos. */
  sendAttribution: boolean;
  isCancelled: () => boolean;
};

/**
 * Reads session infos (or writes attribution) and user data for a valid JWT. Failed or hung PSI calls resolve
 * to null so the result always carries the JWT-derived auth state.
 */
async function loadSessionState(jwt: string, { sendAttribution, isCancelled }: LoadOptions): Promise<ClientSessionState | null> {
  if (isCancelled()) return null;

  const token = parseJwtToken(jwt);
  const isAuthenticated = isUserAuthenticated(token);
  const uuid = token?.uuid ?? null;
  const { pref, voucher, referrer } = readAttributionParams(window.location.search, document.referrer);

  const sessionPromise = withTimeout(
    sendAttribution && (pref || voucher)
      ? sessionClient.setSessionInfos({ pref, voucher, referrer })
      : sessionClient.getSessionInfos(),
    SESSION_REQUEST_TIMEOUT_MS,
  ).catch(error => {
    logger.warn({ error }, '[useClientSession] Failed to fetch session infos');

    return null;
  });

  const userDataPromise = (
    isAuthenticated && uuid
      ? withTimeout(sessionClient.getUserData(uuid), SESSION_REQUEST_TIMEOUT_MS)
      : Promise.resolve(null)
  ).catch(error => {
    logger.warn({ error }, '[useClientSession] Failed to fetch user data');

    return null;
  });

  const [sessionInfos, userData] = await Promise.all([sessionPromise, userDataPromise]);

  return { status: CLIENT_SESSION_STATUS.READY, isAuthenticated, uuid, sessionInfos, userData };
}

/**
 * Browser counterpart of SessionStreaming: applies attribution and loads user data once useSessionJwt has a session.
 *
 * Order matters: the JWT must exist before setSessionInfos, otherwise the campaign attaches to a session the
 * browser never keeps. A failed or hung PSI call never blocks the result: the Jentis page view waits for the env
 * extension, so the state always becomes ready with the auth state derived from the JWT.
 *
 * Ready-as-anonymous (no JWT in time) unblocks the page view; the late session then arrives as a new version and
 * attribution still reaches the session the browser keeps. The second setState re-runs the applier's USER update
 * and notifyExtended(); the page view is not tracked twice (it is bound to onceExtended + pathname).
 *
 * A login (afterLogin) reloads the state without attribution and without going back to loading: on non-cart pages
 * the header keeps this component mounted, so nothing else would update PIXUM_ENV.USER and the account slot until
 * the next full page load. A login that cannot read the session keeps the current state.
 */
export function useClientSession(): ClientSessionState {
  const { status, jwt, version, afterLogin } = useSessionJwt();
  const [state, setState] = useState<ClientSessionState>(INITIAL_STATE);

  useEffect(() => {
    if (status === SESSION_JWT_STATUS.LOADING) return;

    // No session in time: ready as anonymous, but keep the data of an earlier session after a login.
    if (!jwt) {
      if (!afterLogin) setState({ ...INITIAL_STATE, status: CLIENT_SESSION_STATUS.READY });

      return;
    }

    // Per run, so the discarded first run of a Strict Mode double mount sends no attribution.
    let cancelled = false;

    // Attribution is written once per page, for the first session only.
    const sendAttribution = !afterLogin;

    void loadSessionState(jwt, { sendAttribution, isCancelled: () => cancelled }).then(next => {
      if (next && !cancelled) setState(next);
    });

    return () => {
      cancelled = true;
    };
    // version, not jwt: a repeat login can deliver the same JWT string and must still reload.
  }, [status, version]);

  return state;
}
